import React, { FormEvent, useEffect } from "react"
import { Box, Grid, Modal } from "@mui/material"
import RoundedPanel, { CrossCheckbox } from "common/RoundedPanel"
import { ModalProps } from "./common"
import { SimpleGatedCommunityRecord, GatedCommunityRecord, NmtEngine, getEnabledNmtEngines } from "api/gatedCommunity"
import { NormalTextField } from "common/TextField"
import { SumbitButtons } from "common/SubmitButtons"
import { useDataProvider, useRefresh, useNotify } from "ra-core"
import { CustomSelectInput } from "common/OutlinedSelectInput"
import { CustomMultiselect } from "common/OutlinedArrayInput"

export const EditGC = ({ record, open, onClose }: ModalProps<GatedCommunityRecord>) => {
  const [recyclableTranslationLevel, setRecyclableTranslationLevel] = React.useState<string>(record.recyclableTranslationLevel)
  const [isLoading, setIsLoading] = React.useState<boolean>(false)
  const [availableEngines, setAvailableEngines] = React.useState<NmtEngine[]>([])
  const [selectedEngines, setSelectedEngines] = React.useState<NmtEngine[]>(record.translationEngines || [])

  const dataProvider = useDataProvider()
  const refresh = useRefresh()
  const notify = useNotify()

  useEffect(() => {
    if (open) {
      getEnabledNmtEngines()
        .then(engines => setAvailableEngines(engines))
        .catch(e => notify(e.message || 'Failed to load NMT engines', { type: 'error' }))
    }
  }, [open, notify])

  useEffect(() => {
    if (record && open) {
      setSelectedEngines(record.translationEngines || [])
    }
  }, [record, open])

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const data: SimpleGatedCommunityRecord = {
      ...record,
      name: e.currentTarget.GCname.value,
      recyclableTranslationLevel: recyclableTranslationLevel,
      anonymizationEnabled: e.currentTarget.anonymizationEnabled.checked,
      aiEnhancerEnabled: e.currentTarget.aiEnhancerEnabled.checked,
      translationEngines: selectedEngines
    }

    const updateQuery = {
      id: record.id,
      data: data,
      previousData: record
    }

    setIsLoading(true)

    dataProvider
      .update<GatedCommunityRecord>("gatedCommunities", updateQuery)
      .then(() => {
        notify('Gated Community was successfully updated.', { type: 'success' })
      })
      .catch((e) => {
        notify(e.message || 'Something went wrong', { type: 'error' })
      })
      .finally(() => {
        setIsLoading(false)
        refresh()
        onClose()
      })
  }
  return <Modal
    open={open}
    onClose={onClose}
    disableAutoFocus={true}
  >
    <RoundedPanel modal width="500px">
      <h2>Edit Gated Community</h2>
      <hr />
      <form
        onSubmit={handleSave}
      >
        <Grid container spacing={'20px'}>
          <Grid item xs={12}>
            <NormalTextField
              id="GCname"
              label="Gated Community Name"
              defaultValue={record.name}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <CustomSelectInput
              label="Recyclable Translation Level"
              choices={[{ id: 'TRUST_MINED', name: 'Trust Mined' }, { id: 'ENHANCED', name: 'Enhanced' }]}
              initValue={recyclableTranslationLevel}
              onChange={setRecyclableTranslationLevel}
              fullWidth
              clearable={false}
              alwaysOn={true}
              skipTranslate={true}
            />
          </Grid>
          {availableEngines.length > 0 && <Grid item xs={12}>
            <CustomMultiselect
              key={`${record.id}-${open}`}
              label="Select NMT engines"
              choices={availableEngines}
              defaultValue={selectedEngines}
              onChange={setSelectedEngines}
              getOptionLabel={(engine) => engine.descriptiveName}
              fullWidth
              skipTranslate={true}
              dynamicHeight={true}
            />
          </Grid>}
          <Grid item xs={12}>
            <CrossCheckbox defaultChecked={record.anonymizationEnabled} id="anonymizationEnabled" />Anonymization Enabled<br />
            <CrossCheckbox defaultChecked={record.aiEnhancerEnabled} id="aiEnhancerEnabled" />AI Enhancer Enabled
          </Grid>
        </Grid>
        <Box height={20} />
        <hr />
        <SumbitButtons
          isLoading={isLoading}
          onClose={onClose}
        />
      </form>

    </RoundedPanel>
  </Modal>
}